/**
 * AudienceGuessSheet: M5.T11.
 *
 * Wraps <GuessInput> with a mobile-portrait swipe-up <dialog> sheet so a
 * phone-shaped audience member can keep the canvas visible while the
 * software keyboard is up.
 *
 *   - Wide viewport / landscape / tablet+ → renders <GuessInput> inline
 *     exactly as before. No behavior change.
 *   - Mobile portrait (matches the same media query as M5.T10's drawer
 *     layout: `(max-width: 480px) and (orientation: portrait)`) → renders
 *     a compact trigger bar at the sticky-bottom. Tapping the trigger
 *     opens a native <dialog> sheet that holds the full <GuessInput>.
 *     A correct guess auto-closes the sheet so the player can see the
 *     drawer's final strokes; the trigger row then shows the disabled
 *     "You got it!" state.
 *
 * Why a <dialog> instead of an always-visible bottom strip:
 *   On iOS Safari the software keyboard pushes a sticky-bottom input up
 *   over the canvas, eating the entire viewport when typing. Routing
 *   the input through a modal sheet lets the layout decide what to show
 *   when the keyboard is down (trigger only) vs. up (full sheet) and
 *   gives us a natural close-and-resume flow after a verdict.
 *
 * The media-query check is JS-side via matchMedia so the trigger element
 * isn't even rendered on desktop. CSS-only show/hide would still ship
 * the dialog DOM (cheap but adds a11y tree noise on every audience
 * client), and rendering conditionally lets the keyboard-handling
 * tests assert behavior without media-query stubbing.
 */

import { useEffect, useRef, useState } from "preact/hooks";
import { GuessInput } from "./GuessInput.js";
import { ArrowDoodle } from "../marks/v1/ArrowDoodle.js";
import type { ClientControlFrame } from "../ws.js";

type Verdict = { kind: "correct" | "close" | "miss"; text: string; at: number };

type AudienceGuessSheetProps = {
  sendFrame: (frame: ClientControlFrame) => boolean;
  disabled?: boolean;
  lastVerdict?: Verdict;
};

// Audit 2026-06-11: landscape phones joined the phone layout (the CSS block
// in index.html carries the same compound query), so the sheet gates on both
// arms. The export keeps its historical name.
export const MOBILE_PORTRAIT_MQ =
  "(max-width: 480px) and (orientation: portrait), (max-height: 480px) and (orientation: landscape)";

function readMatches(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(MOBILE_PORTRAIT_MQ).matches;
}

function useMatchesMobilePortrait(): boolean {
  const [matches, setMatches] = useState<boolean>(readMatches);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(MOBILE_PORTRAIT_MQ);
    const onChange = () => setMatches(mql.matches);
    onChange();
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    // Safari < 14 fallback.
    const legacy = mql as unknown as {
      addListener: (cb: () => void) => void;
      removeListener: (cb: () => void) => void;
    };
    legacy.addListener(onChange);
    return () => legacy.removeListener(onChange);
  }, []);
  return matches;
}

const VERDICT_HINT: Record<Verdict["kind"], string> = {
  correct: "got it",
  close: "so close",
  miss: "not quite",
};

export function AudienceGuessSheet({
  sendFrame,
  disabled = false,
  lastVerdict,
}: AudienceGuessSheetProps) {
  const isMobilePortrait = useMatchesMobilePortrait();
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Auto-close on correct verdict so the canvas is unobscured for the
  // final-stroke flourish. Re-key on `at` so a fresh correct (across
  // turns) re-fires even if `kind` was already "correct".
  useEffect(() => {
    if (!lastVerdict) return;
    if (lastVerdict.kind === "correct") setOpen(false);
  }, [lastVerdict?.at, lastVerdict?.kind]);

  // Imperatively sync the dialog's open state. <dialog open={…}> works in
  // Preact but doesn't trigger the modal behavior (top layer + ESC + scrim);
  // call showModal()/close() instead.
  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (open && !dlg.open) {
      if (typeof dlg.showModal === "function") {
        try {
          dlg.showModal();
        } catch {
          dlg.setAttribute("open", "");
        }
      } else {
        dlg.setAttribute("open", "");
      }
      // showModal() focuses the first focusable, the × close button, so
      // typing a guess took a second tap (audit 2026-06-11). Focus the
      // input: one tap on "Tap to guess…" → keyboard up, ready to type.
      dlg.querySelector<HTMLInputElement>("input")?.focus();
    } else if (!open && dlg.open) {
      if (typeof dlg.close === "function") dlg.close();
      else dlg.removeAttribute("open");
    }
  }, [open]);

  if (!isMobilePortrait) {
    return (
      <GuessInput
        sendFrame={sendFrame}
        disabled={disabled}
        {...(lastVerdict ? { lastVerdict } : {})}
      />
    );
  }

  const verdictHint = lastVerdict ? VERDICT_HINT[lastVerdict.kind] : "";

  return (
    <div class="audience-guess-sheet" data-testid="audience-guess-sheet">
      <button
        type="button"
        class="scribble-btn audience-guess-sheet-trigger"
        data-testid="audience-guess-sheet-trigger"
        onClick={() => {
          // @testing-library/preact's fireEvent.click dispatches a synthetic
          // click that bypasses the `disabled` short-circuit jsdom applies on
          // native clicks, so guard here too.
          if (disabled) return;
          setOpen(true);
        }}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open ? "true" : "false"}
      >
        <span class="audience-guess-sheet-trigger-label">
          {/* The idle ArrowDoodle points at the action (audit 2026-06-11,
              the mark library sat 8/12 unused while the primary mobile
              action had no visual pull). */}
          {!disabled && (
            <span class="audience-guess-sheet-trigger-arrow" aria-hidden="true">
              <ArrowDoodle size={18} />
            </span>
          )}{" "}
          {disabled ? "You got it!" : "Tap to guess…"}
        </span>
        {verdictHint && !disabled && (
          <span
            class={`audience-guess-sheet-trigger-hint audience-guess-sheet-trigger-hint--${lastVerdict?.kind}`}
            data-testid="audience-guess-sheet-trigger-hint"
          >
            {verdictHint}
          </span>
        )}
      </button>
      <dialog
        ref={dialogRef}
        class="audience-guess-sheet-dialog"
        data-testid="audience-guess-sheet-dialog"
        aria-label="Guess panel"
        onClose={() => setOpen(false)}
        onCancel={(ev) => {
          // ESC fires both onCancel and onClose; let onClose win.
          ev.preventDefault();
          setOpen(false);
        }}
      >
        <div class="audience-guess-sheet-handle" aria-hidden="true" />
        <button
          type="button"
          class="audience-guess-sheet-close"
          data-testid="audience-guess-sheet-close"
          onClick={() => setOpen(false)}
          aria-label="Close guess panel"
        >
          ×
        </button>
        <GuessInput
          sendFrame={sendFrame}
          disabled={disabled}
          {...(lastVerdict ? { lastVerdict } : {})}
        />
      </dialog>
    </div>
  );
}
